import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  FormControlLabel,
  Switch,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  RadioGroup,
  Radio,
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  Refresh,
  Timeline,
  Settings,
  ModelTraining,
  CheckCircle,
  Error,
  Warning,
  Schedule,
  CalendarMonth,
} from '@mui/icons-material';

import { CreditRiskAPI } from '../services/api';
import { TrainingStatus, TrainingRequest } from '../types';

interface TrainingManagementProps {
  onNotification: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

interface TrainingSchedule {
  enabled: boolean;
  frequency: 'daily' | 'weekly' | 'monthly';
  dayOfWeek?: number; // 0-6 for weekly
  dayOfMonth?: number; // 1-31 for monthly
  hour: number; // 0-23
  performanceThreshold?: number; // Only retrain if performance drops below this
  lastRun?: Date;
  nextRun?: Date;
}

const TrainingManagement: React.FC<TrainingManagementProps> = ({ onNotification }) => {
  const [trainingSessions, setTrainingSessions] = useState<TrainingStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [isTraining, setIsTraining] = useState(false);
  const [startTrainingDialog, setStartTrainingDialog] = useState(false);
  const [scheduleDialog, setScheduleDialog] = useState(false);

  // Simplified training form - trains both models
  const [trainingForm, setTrainingForm] = useState({
    algorithm: 'random_forest', // As per the actual implementation
    optimize_hyperparams: false,
    cross_validation: true,
    test_size: 0.2,
    random_state: 42,
    handle_imbalance: true,
    generate_report: true,
    description: '',
  });

  const [schedule, setSchedule] = useState<TrainingSchedule>({
    enabled: false,
    frequency: 'weekly',
    dayOfWeek: 1, // Monday
    hour: 2, // 2 AM
    performanceThreshold: 0.70,
  });

  // Algorithm options (matching the actual training script)
  const algorithmOptions = [
    { value: 'random_forest', label: 'Random Forest' },
    { value: 'xgboost', label: 'XGBoost' },
    { value: 'logistic_regression', label: 'Logistic Regression' },
    { value: 'lightgbm', label: 'LightGBM' },
    { value: 'catboost', label: 'CatBoost' },
  ];

  const frequencyOptions = [
    { value: 'daily', label: 'Diariamente' },
    { value: 'weekly', label: 'Semanalmente' },
    { value: 'monthly', label: 'Mensualmente' },
  ];

  const dayOptions = [
    { value: 1, label: 'Lunes' },
    { value: 2, label: 'Martes' },
    { value: 3, label: 'Miércoles' },
    { value: 4, label: 'Jueves' },
    { value: 5, label: 'Viernes' },
    { value: 6, label: 'Sábado' },
    { value: 0, label: 'Domingo' },
  ];

  useEffect(() => {
    loadTrainingSessions();
    loadSchedule();
  }, []);

  const loadTrainingSessions = () => {
    // In a real implementation, this would fetch from API
    setTrainingSessions([]);
  };

  const loadSchedule = () => {
    // Load saved schedule from localStorage or API
    const savedSchedule = localStorage.getItem('trainingSchedule');
    if (savedSchedule) {
      setSchedule(JSON.parse(savedSchedule));
    }
  };

  const handleStartTraining = async () => {
    setIsTraining(true);
    try {
      // Start training both models via the shared client (correct origin + auth
      // token). axios throws on non-2xx, so failures fall through to catch.
      const data = await CreditRiskAPI.startTrainingAll({
        models: ['loan_default', 'credit_scorecard'], // Train both models
        ...trainingForm
      });

      onNotification('Entrenamiento de ambos modelos iniciado exitosamente', 'success');

      // Add to training sessions
      const newSession: TrainingStatus = {
        training_id: data.training_id || `training_${Date.now()}`,
        status: 'running',
        model_type: 'both_models', // Changed to indicate both models
        algorithm: trainingForm.algorithm,
        progress: 0,
        started_at: new Date().toISOString(),
        current_step: 'Iniciando entrenamiento...',
        total_steps: 7,
        metrics: {},
      };
      setTrainingSessions(prev => [newSession, ...prev]);
      setStartTrainingDialog(false);

      // Simulate progress (in real app, would poll for status)
      simulateTrainingProgress(newSession.training_id);
    } catch (error) {
      onNotification('Error al iniciar el entrenamiento', 'error');
    } finally {
      setIsTraining(false);
    }
  };

  const simulateTrainingProgress = (trainingId: string) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setTrainingSessions(prev =>
        prev.map(session =>
          session.training_id === trainingId
            ? { ...session, progress, status: progress >= 100 ? 'completed' : 'running' }
            : session
        )
      );
      if (progress >= 100) {
        clearInterval(interval);
        onNotification('Entrenamiento completado exitosamente', 'success');
      }
    }, 2000);
  };

  const handleSaveSchedule = () => {
    // Calculate next run time
    const nextRun = calculateNextRun(schedule);
    const updatedSchedule = { ...schedule, nextRun };

    // Save to localStorage (in real app, would save to backend)
    localStorage.setItem('trainingSchedule', JSON.stringify(updatedSchedule));
    setSchedule(updatedSchedule);

    if (schedule.enabled) {
      onNotification('Programación de entrenamiento activada', 'success');
      // In a real implementation, this would register the schedule with a backend service
    } else {
      onNotification('Programación de entrenamiento desactivada', 'info');
    }

    setScheduleDialog(false);
  };

  const calculateNextRun = (schedule: TrainingSchedule): Date => {
    const now = new Date();
    const next = new Date();

    switch (schedule.frequency) {
      case 'daily':
        next.setDate(now.getDate() + 1);
        break;
      case 'weekly':
        const daysUntilNext = ((schedule.dayOfWeek || 1) - now.getDay() + 7) % 7 || 7;
        next.setDate(now.getDate() + daysUntilNext);
        break;
      case 'monthly':
        next.setMonth(now.getMonth() + 1);
        next.setDate(schedule.dayOfMonth || 1);
        break;
    }

    next.setHours(schedule.hour, 0, 0, 0);
    return next;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'running': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle />;
      case 'running': return <CircularProgress size={20} />;
      case 'failed': return <Error />;
      default: return <Warning />;
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Gestión de Entrenamiento
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadTrainingSessions}
          >
            Actualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<ModelTraining />}
            onClick={() => setStartTrainingDialog(true)}
            color="primary"
          >
            Nuevo Entrenamiento
          </Button>
          <Button
            variant="contained"
            startIcon={<Schedule />}
            onClick={() => setScheduleDialog(true)}
            color="secondary"
          >
            Programar
          </Button>
        </Box>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Inicie nuevos entrenamientos de modelos, monitoree el progreso y programe entrenamientos automáticos.
      </Typography>

      <Grid container spacing={3}>
        {/* Quick Start Training */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Entrenamiento Rápido
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Entrena ambos modelos (Loan Default y Credit Scorecard) simultáneamente
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Chip
                  icon={<ModelTraining />}
                  label="Modelos: Loan Default + Credit Scorecard"
                  color="primary"
                  sx={{ mb: 2 }}
                />
              </Box>

              <FormControl fullWidth size="small">
                <InputLabel>Algoritmo</InputLabel>
                <Select
                  value={trainingForm.algorithm}
                  onChange={(e) => setTrainingForm({ ...trainingForm, algorithm: e.target.value })}
                  label="Algoritmo"
                >
                  {algorithmOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
            <CardActions>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={() => setStartTrainingDialog(true)}
                disabled={isTraining}
              >
                Iniciar Entrenamiento
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Training Statistics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Estadísticas de Entrenamiento
              </Typography>
              <Grid container spacing={2} sx={{ textAlign: 'center' }}>
                <Grid item xs={3}>
                  <Typography variant="h4" color="primary">
                    {trainingSessions.filter(s => s.status === 'completed').length}
                  </Typography>
                  <Typography variant="caption">Completados</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="h4" color="warning.main">
                    {trainingSessions.filter(s => s.status === 'running').length}
                  </Typography>
                  <Typography variant="caption">En Progreso</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="h4" color="error.main">
                    {trainingSessions.filter(s => s.status === 'failed').length}
                  </Typography>
                  <Typography variant="caption">Fallidos</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="h4" color="text.secondary">
                    {trainingSessions.length}
                  </Typography>
                  <Typography variant="caption">Total</Typography>
                </Grid>
              </Grid>

              {schedule.enabled && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Próximo entrenamiento programado: {' '}
                    {schedule.nextRun ? new Date(schedule.nextRun).toLocaleString('es-ES') : 'No programado'}
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Active Training Sessions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sesiones de Entrenamiento Activas
              </Typography>

              {trainingSessions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <ModelTraining sx={{ fontSize: 48, color: 'text.secondary' }} />
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                    No hay sesiones de entrenamiento activas
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Inicie un nuevo entrenamiento para comenzar
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Modelos</TableCell>
                        <TableCell>Algoritmo</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Progreso</TableCell>
                        <TableCell>Iniciado</TableCell>
                        <TableCell>Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {trainingSessions.map((session) => (
                        <TableRow key={session.training_id}>
                          <TableCell>{session.training_id.substring(0, 8)}...</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label="Loan Default + Scorecard"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{session.algorithm}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              icon={getStatusIcon(session.status)}
                              label={session.status}
                              color={getStatusColor(session.status) as any}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <LinearProgress
                                variant="determinate"
                                value={session.progress}
                                sx={{ width: '100px', mr: 1 }}
                              />
                              <Typography variant="body2">{session.progress}%</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {new Date(session.started_at).toLocaleString('es-ES')}
                          </TableCell>
                          <TableCell>
                            {session.status === 'running' && (
                              <Button size="small" color="error" startIcon={<Stop />}>
                                Detener
                              </Button>
                            )}
                            {session.status === 'completed' && (
                              <Button size="small" startIcon={<Timeline />}>
                                Ver Métricas
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Start Training Dialog */}
      <Dialog open={startTrainingDialog} onClose={() => setStartTrainingDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Configurar Nuevo Entrenamiento</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Se entrenarán ambos modelos (Loan Default y Credit Scorecard) con la misma configuración
          </Alert>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Algoritmo</InputLabel>
                <Select
                  value={trainingForm.algorithm}
                  onChange={(e) => setTrainingForm({ ...trainingForm, algorithm: e.target.value })}
                  label="Algoritmo"
                >
                  {algorithmOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={trainingForm.optimize_hyperparams}
                    onChange={(e) => setTrainingForm({ ...trainingForm, optimize_hyperparams: e.target.checked })}
                  />
                }
                label="Optimizar Hiperparámetros"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={trainingForm.cross_validation}
                    onChange={(e) => setTrainingForm({ ...trainingForm, cross_validation: e.target.checked })}
                  />
                }
                label="Validación Cruzada"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={trainingForm.handle_imbalance}
                    onChange={(e) => setTrainingForm({ ...trainingForm, handle_imbalance: e.target.checked })}
                  />
                }
                label="Manejar Desbalance de Clases"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={trainingForm.generate_report}
                    onChange={(e) => setTrainingForm({ ...trainingForm, generate_report: e.target.checked })}
                  />
                }
                label="Generar Reporte"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción (Opcional)"
                multiline
                rows={3}
                value={trainingForm.description}
                onChange={(e) => setTrainingForm({ ...trainingForm, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartTrainingDialog(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleStartTraining}
            variant="contained"
            startIcon={<PlayArrow />}
            disabled={isTraining}
          >
            {isTraining ? 'Iniciando...' : 'Iniciar Entrenamiento'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Training Dialog */}
      <Dialog open={scheduleDialog} onClose={() => setScheduleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarMonth />
            Programar Entrenamiento Automático
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Solo puede haber una programación activa a la vez. Activar una nueva programación desactivará la anterior.
          </Alert>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={schedule.enabled}
                    onChange={(e) => setSchedule({ ...schedule, enabled: e.target.checked })}
                    color="primary"
                  />
                }
                label="Activar programación automática"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth disabled={!schedule.enabled}>
                <InputLabel>Frecuencia</InputLabel>
                <Select
                  value={schedule.frequency}
                  onChange={(e) => setSchedule({ ...schedule, frequency: e.target.value as any })}
                  label="Frecuencia"
                >
                  {frequencyOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {schedule.frequency === 'weekly' && (
              <Grid item xs={12}>
                <FormControl fullWidth disabled={!schedule.enabled}>
                  <InputLabel>Día de la Semana</InputLabel>
                  <Select
                    value={schedule.dayOfWeek}
                    onChange={(e) => setSchedule({ ...schedule, dayOfWeek: e.target.value as number })}
                    label="Día de la Semana"
                  >
                    {dayOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {schedule.frequency === 'monthly' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Día del Mes"
                  value={schedule.dayOfMonth || 1}
                  onChange={(e) => setSchedule({
                    ...schedule,
                    dayOfMonth: Math.min(31, Math.max(1, parseInt(e.target.value) || 1))
                  })}
                  disabled={!schedule.enabled}
                  InputProps={{ inputProps: { min: 1, max: 31 } }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Hora del Día (0-23)"
                value={schedule.hour}
                onChange={(e) => setSchedule({
                  ...schedule,
                  hour: Math.min(23, Math.max(0, parseInt(e.target.value) || 0))
                })}
                disabled={!schedule.enabled}
                InputProps={{ inputProps: { min: 0, max: 23 } }}
                helperText="Hora en formato 24h (0 = medianoche, 12 = mediodía)"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Umbral de Rendimiento"
                value={schedule.performanceThreshold || 0.70}
                onChange={(e) => setSchedule({
                  ...schedule,
                  performanceThreshold: parseFloat(e.target.value) || 0.70
                })}
                disabled={!schedule.enabled}
                InputProps={{ inputProps: { min: 0, max: 1, step: 0.01 } }}
                helperText="Solo reentrenar si el rendimiento cae por debajo de este valor"
              />
            </Grid>

            {schedule.enabled && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Próximo entrenamiento: {calculateNextRun(schedule).toLocaleString('es-ES')}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialog(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveSchedule}
            variant="contained"
            startIcon={<Schedule />}
          >
            Guardar Programación
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingManagement;